// Tarjeta de contenido reutilizable que muestra un post traído de Ghost CMS.
// Se usa en las páginas de Datos, Teatro y Perfumería para evitar duplicar código.
//
// Props:
//   item    — objeto post de Ghost (slug, title, excerpt, feature_image, published_at)
//   variant — 'card' (default, grid vertical) | 'compact' (grid compacta) | 'list' (fila horizontal)

import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import styles from './InfoCard.module.css';

// Convierte una fecha ISO en texto legible en español (ej: "15 de mayo de 2025")
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

const InfoCard = memo(({ item, variant = 'card' }) => {
  const { slug, title, excerpt, feature_image, published_at } = item;

  // ── Variante LISTA — fila horizontal compacta para secciones de artículos ──
  if (variant === 'list') {
    return (
      <Link to={`/post/${slug}`} className={styles['card-list']} aria-label={`Ver ${title}`}>
        {feature_image && (
          <div className={styles['card-list-img-wrap']}>
            <img src={feature_image} alt={title} className={styles['card-list-img']} loading="lazy" />
          </div>
        )}
        <div className={styles['card-list-body']}>
          <h3 className={styles['card-list-title']}>{title}</h3>
          <p className={styles['card-list-excerpt']}>{excerpt}</p>
          <time className={styles['card-list-date']} dateTime={published_at}>
            {formatDate(published_at)}
          </time>
        </div>
      </Link>
    );
  }

  // ── Variante COMPACT — tarjeta de grid con imagen fija de menor altura ────
  if (variant === 'compact') {
    return (
      <Link to={`/post/${slug}`} className={styles['card-compact']} aria-label={`Ver ${title}`}>
        {feature_image ? (
          <div className={styles['card-compact-img-wrap']}>
            <img src={feature_image} alt={title} className={styles['card-compact-img']} loading="lazy" />
          </div>
        ) : (
          <div className={styles['card-compact-img-placeholder']} />
        )}
        <div className={styles['card-compact-body']}>
          <h3 className={styles['card-compact-title']}>{title}</h3>
          <p className={styles['card-compact-excerpt']}>{excerpt}</p>
          <time className={styles['card-compact-date']} dateTime={published_at}>
            {formatDate(published_at)}
          </time>
        </div>
      </Link>
    );
  }

  // ── Variante CARD (default) — tarjeta vertical clásica ───────────────────
  return (
    <div className={styles['card-wrapper']}>
      <Link to={`/post/${slug}`} className={styles['card']} aria-label={`Ver ${title}`}>
        {feature_image && (
          <div className={styles['card-image-container']}>
            <img
              src={feature_image}
              alt={title}
              className={styles['card-image']}
              loading="lazy"
            />
          </div>
        )}
        <div className={styles['card-content']}>
          <h3 className={styles['card-title']}>{title}</h3>
          <p className={styles['card-description']}>{excerpt}</p>
          <time className={styles['card-date']} dateTime={published_at}>
            {formatDate(published_at)}
          </time>
        </div>
      </Link>
    </div>
  );
});

export default InfoCard;
